import type {
  AdminJiraSettings,
  AdminJiraSettingsPatch,
} from "../../../lib/api";

/** Jira project keys: same rule as the backend (`settings.py::PROJECT_KEY_PATTERN`). */
export const PROJECT_KEY_PATTERN = /^[A-Z][A-Z0-9_]{0,254}$/;

/** Parsed project-key text: valid keys (normalized) and the raw tokens that failed. */
export type ParsedProjectKeys = { keys: string[]; invalid: string[] };

/** Split on commas/whitespace, uppercase, dedupe (keeping order), and flag invalid tokens. */
export function parseProjectKeys(text: string): ParsedProjectKeys {
  const keys: string[] = [];
  const invalid: string[] = [];
  for (const token of text.split(/[\s,]+/)) {
    const raw = token.trim();
    if (!raw) continue;
    const key = raw.toUpperCase();
    if (!PROJECT_KEY_PATTERN.test(key)) {
      if (!invalid.includes(raw)) invalid.push(raw);
    } else if (!keys.includes(key)) {
      keys.push(key);
    }
  }
  return { keys, invalid };
}

/** The editable subset of the settings, as held by the form. */
export type JiraSettingsForm = Pick<
  AdminJiraSettings,
  | "enabled"
  | "allowed_projects"
  | "max_issues_per_query"
  | "per_user_queries_per_hour"
  | "default_cache_ttl_seconds_cap"
>;

const NUMBER_KEYS = [
  "max_issues_per_query",
  "per_user_queries_per_hour",
  "default_cache_ttl_seconds_cap",
] as const;

/** One numeric cap the form edits. */
export type CapKey = (typeof NUMBER_KEYS)[number];

/** Backend limit (`settings.py`), pinned by `backend/tests/test_jira_contract.py`. */
export const MAX_ALLOWED_PROJECTS = 100;

/** Highest value each cap accepts (`JiraSettingsPatch`); `null` means no upper bound. */
export const CAP_MAXIMUMS = {
  max_issues_per_query: 1000,
  per_user_queries_per_hour: null,
  default_cache_ttl_seconds_cap: 86_400,
} as const;

/**
 * Caps the backend would reject: blank (NaN), fractional, below 1, above the
 * maximum, or past 2^53 (not an exact integer, and more than Firestore can store).
 */
export function invalidCaps(caps: Pick<JiraSettingsForm, CapKey>): CapKey[] {
  return NUMBER_KEYS.filter((key) => {
    const value = caps[key];
    const max = CAP_MAXIMUMS[key];
    return (
      !Number.isSafeInteger(value) || value < 1 || (max !== null && value > max)
    );
  });
}

/** Minimal PUT body: only the fields that differ from the saved settings. */
export function settingsPatch(
  saved: AdminJiraSettings,
  form: JiraSettingsForm,
): AdminJiraSettingsPatch {
  const patch: AdminJiraSettingsPatch = {};
  if (form.enabled !== saved.enabled) patch.enabled = form.enabled;
  for (const key of NUMBER_KEYS) {
    // Safe union-key write: all three keys are `number` in both types.
    if (form[key] !== saved[key]) patch[key] = form[key];
  }
  if (form.allowed_projects.join(",") !== saved.allowed_projects.join(",")) {
    patch.allowed_projects = form.allowed_projects;
  }
  return patch;
}
